import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface User {
  id: string;
  username: string;
  role: 'admin' | 'entrega';
  permissions: string[];
}

interface AuthContextType {
  user: User | null;
  sessionId: string | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<{ error?: string }>;
  logout: () => Promise<void>;
  hasPermission: (module: string) => boolean;
  hasAnyPermission: (modules: string[]) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Get the API URL from the Supabase client configuration
const getApiUrl = () => {
  const supabaseUrl = (supabase as any).supabaseUrl || 
    import.meta.env.VITE_SUPABASE_URL || 
    'https://jifwgrixvsqmimrvqwhp.supabase.co';
  return `${supabaseUrl}/functions/v1`;
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const storedSessionId = document.cookie
      .split('; ')
      .find(row => row.startsWith('sessionId='))
      ?.split('=')[1];

    console.log('[Auth] Init - stored session:', storedSessionId ? 'exists' : 'none');

    if (storedSessionId) {
      verifySession(storedSessionId);
    } else {
      setLoading(false);
    }
  }, []);

  const verifySession = async (sid: string) => {
    console.log('[Auth] Verifying session...');
    try {
      const response = await fetch(`${getApiUrl()}/auth/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId: sid }),
      });
      const data = await response.json();
      
      console.log('[Auth] Verify response:', { valid: data.valid, user: data.user?.username, permissions: data.user?.permissions });
      
      if (data.valid) {
        setUser({
          ...data.user,
          permissions: data.user.permissions || []
        });
        setSessionId(sid);
      } else {
        console.log('[Auth] Session invalid, clearing cookie');
        document.cookie = 'sessionId=; Max-Age=0; path=/';
      }
    } catch (error) {
      console.error('[Auth] Session verification failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const login = async (username: string, password: string) => {
    console.log('[Auth] Login attempt for user:', username);
    try {
      const response = await fetch(`${getApiUrl()}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        console.log('[Auth] Login failed:', data.error);
        return { error: data.error || 'Erro ao fazer login' };
      }

      console.log('[Auth] Login successful:', { user: data.user?.username, role: data.user?.role, permissions: data.user?.permissions });

      // Use SameSite=None; Secure on HTTPS so the cookie persists inside
      // the Lovable preview iframe (cross-site context). Fall back to Lax on http.
      const isSecure = window.location.protocol === 'https:';
      const cookieAttrs = isSecure
        ? 'Secure; SameSite=None'
        : 'SameSite=Lax';
      document.cookie = `sessionId=${data.sessionId}; path=/; max-age=${24 * 60 * 60}; ${cookieAttrs}`;
      setSessionId(data.sessionId);
      setUser({
        ...data.user,
        permissions: data.user.permissions || []
      });
      return {};
    } catch (error) {
      console.error('[Auth] Login error:', error);
      return { error: 'Servidor indisponível. Verifique sua conexão e tente novamente.' };
    }
  };

  const logout = async () => {
    console.log('[Auth] Logout initiated');
    if (sessionId) {
      try {
        await fetch(`${getApiUrl()}/auth/logout`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sessionId }),
        });
        console.log('[Auth] Logout successful');
      } catch (error) {
        console.error('[Auth] Logout error:', error);
      }
    }
    document.cookie = 'sessionId=; Max-Age=0; path=/';
    setUser(null);
    setSessionId(null);
  };

  // Check if user has permission for a specific module
  const hasPermission = useCallback((module: string): boolean => {
    if (!user) return false;
    // Admins have access to everything
    if (user.role === 'admin') return true;
    // Check if module is in user's permissions
    return user.permissions.includes(module);
  }, [user]);

  // Check if user has permission for any of the given modules
  const hasAnyPermission = useCallback((modules: string[]): boolean => {
    if (!user) return false;
    // Admins have access to everything
    if (user.role === 'admin') return true;
    // Check if any module is in user's permissions
    return modules.some(module => user.permissions.includes(module));
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, sessionId, loading, login, logout, hasPermission, hasAnyPermission }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}